import React, { useEffect, useState } from 'react';
import theFoxMini from '../../TheFoxMini.png';
import scissorsImage from '../../scissors.webp';
import voguImage from '../../vogu.webp';
import forbsImage from '../../forbs.webp';
import vanitImage from '../../vanit.webp';
import '../../styles/FoxChat.css';

export const FOX_MASK_CUTOUT_LOADER_HINTS = [
  'sharpening scissors...',
  'picking a fox...',
  'warming up glue...',
  'cutting...',
  'slicing...',
  'dicing...',
  'aligning cutouts...',
  'sticking it together...',
];

const MAGAZINE_IMAGES = [voguImage, forbsImage, vanitImage];

export const buildFoxMaskMagazinePair = () => {
  const shuffled = [...MAGAZINE_IMAGES].sort(() => Math.random() - 0.5);
  return {
    left: { src: shuffled[0], rot: (Math.random() * 20 - 10).toFixed(1), dx: (Math.random() * 16 - 8).toFixed(1) },
    right: { src: shuffled[1], rot: (Math.random() * 20 - 10).toFixed(1), dx: (Math.random() * 16 - 8).toFixed(1) },
  };
};

export const buildFoxMaskScissorsOffset = () => ({
  left: { rot: (Math.random() * 60 - 30).toFixed(1), dx: (Math.random() * 30 - 15).toFixed(1), dy: (Math.random() * 20 - 10).toFixed(1) },
  right: { rot: (Math.random() * 60 - 30).toFixed(1), dx: (Math.random() * 30 - 15).toFixed(1), dy: (Math.random() * 20 - 10).toFixed(1) },
});

/**
 * Fox + scissors + magazine cutout loader (FoxChat photo step). When `active`, re-randomizes
 * scissors/magazine offsets and cycles hint text — same timing as FoxChat's photo-processing step.
 */
export default function FoxMaskCutoutLoader({
  active = false,
  showHint = true,
  /** When true, always reserve one hint line so cycling text does not shift content below. */
  reserveHintSpace = false,
  className = '',
}) {
  const [hintIndex, setHintIndex] = useState(0);
  const [magazinePair, setMagazinePair] = useState(buildFoxMaskMagazinePair);
  const [scissorsOffset, setScissorsOffset] = useState(buildFoxMaskScissorsOffset);

  useEffect(() => {
    if (!active) {
      setHintIndex(0);
      return undefined;
    }
    setHintIndex(0);
    let idx = 0;
    let timerId;
    const tick = () => {
      idx += 1;
      const clamped = Math.min(idx, FOX_MASK_CUTOUT_LOADER_HINTS.length - 1);
      setHintIndex(clamped);
      setMagazinePair(buildFoxMaskMagazinePair());
      setScissorsOffset(buildFoxMaskScissorsOffset());
      timerId = setTimeout(tick, 1100);
    };
    timerId = setTimeout(tick, 1100);
    return () => clearTimeout(timerId);
  }, [active]);

  return (
    <div
      className={`fox-chat-photo-loader${className ? ` ${className}` : ''}`}
      aria-live={active ? 'polite' : undefined}
    >
      <div className="fox-chat-photo-loader-row">
        <img
          src={magazinePair.left.src}
          alt=""
          className="fox-chat-photo-loader-magazine"
          style={{
            transform: `rotate(${magazinePair.left.rot}deg) translateX(${magazinePair.left.dx}px)`,
          }}
          aria-hidden
        />
        <img
          src={scissorsImage}
          alt=""
          className="fox-chat-photo-loader-scissors fox-chat-photo-loader-scissors-left"
          style={{
            transform: `scaleX(-1) scaleY(-1) rotate(${scissorsOffset.left.rot}deg) translate(${scissorsOffset.left.dx}px, ${scissorsOffset.left.dy}px)`,
          }}
          aria-hidden
        />
        <img
          src={theFoxMini}
          alt=""
          className="fox-chat-photo-loader-fox"
          aria-hidden
        />
        <img
          src={scissorsImage}
          alt=""
          className="fox-chat-photo-loader-scissors fox-chat-photo-loader-scissors-right"
          style={{
            transform: `scaleY(-1) rotate(${scissorsOffset.right.rot}deg) translate(${scissorsOffset.right.dx}px, ${scissorsOffset.right.dy}px)`,
          }}
          aria-hidden
        />
        <img
          src={magazinePair.right.src}
          alt=""
          className="fox-chat-photo-loader-magazine"
          style={{
            transform: `rotate(${magazinePair.right.rot}deg) translateX(${magazinePair.right.dx}px)`,
          }}
          aria-hidden
        />
      </div>
      {showHint && (reserveHintSpace || active) ? (
        <p
          className="fox-chat-photo-loader-hint"
          aria-hidden={reserveHintSpace && !active}
        >
          {active ? FOX_MASK_CUTOUT_LOADER_HINTS[hintIndex] : '\u00a0'}
        </p>
      ) : null}
    </div>
  );
}
